/**
 * Retry timing for uploads: exponential backoff with jitter, so a thousand
 * phones that lost the same cell tower do not all retry on the same tick,
 * and a server's `Retry-After` always wins when it asks for longer.
 */
export const UPLOAD_RETRY = {
  /** Tries per request, the first included. Waiting for a connection does not use one up. */
  maxAttempts: 5,
  baseMs: 1_000,
  capMs: 20_000,
} as const;

/**
 * Delay before the next try, after `failedAttempts` failures (1-based).
 * "Equal jitter": half the exponential step is fixed, half random, so a
 * retry never fires instantly but still spreads out.
 */
export function uploadRetryDelayMs(
  failedAttempts: number,
  {
    baseMs = UPLOAD_RETRY.baseMs,
    capMs = UPLOAD_RETRY.capMs,
    random = Math.random,
    retryAfterMs,
  }: {
    baseMs?: number;
    capMs?: number;
    random?: () => number;
    retryAfterMs?: number;
  } = {},
): number {
  const step = Math.min(capMs, baseMs * 2 ** Math.max(0, failedAttempts - 1));
  const delay = Math.round(step / 2 + random() * (step / 2));
  return Math.max(delay, retryAfterMs ?? 0);
}
